/**
 * OpenClaw Gateway WebSocket 协议 TypeBox Schema
 *
 * 协议文档: https://open-claw.bot/docs/gateway/protocol/
 * TypeBox 用法: https://open-claw.bot/docs/concepts/typebox/
 *
 * 每条 WS 消息是以下三种帧之一：
 *  - Request:  { type: "req",   id, method, params }
 *  - Response: { type: "res",   id, ok, payload | error }
 *  - Event:    { type: "event", event, payload, seq?, stateVersion? }
 */

import { type Static, Type } from '@sinclair/typebox';

// ─── Primitives ───────────────────────────────────────────────────────────────

const NonEmptyString = Type.String({ minLength: 1 });
const OptionalString = Type.Optional(Type.String());

// ─────────────────────────────────────────────────────────────────────────────
// Frame — 顶层帧结构
// ─────────────────────────────────────────────────────────────────────────────

/** 客户端发出的请求帧 */
export const RequestFrameSchema = Type.Object({
  type: Type.Literal('req'),
  /** 请求 ID，用于匹配响应；客户端自定义，全局唯一即可 */
  id: NonEmptyString,
  /** 方法名 */
  method: NonEmptyString,
  /** 方法参数，可选 */
  params: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
});
export type RequestFrame = Static<typeof RequestFrameSchema>;

/** 服务端返回的响应帧 */
export const ResponseFrameSchema = Type.Object({
  type: Type.Literal('res'),
  id: NonEmptyString,
  ok: Type.Boolean(),
  payload: Type.Optional(Type.Unknown()),
  error: Type.Optional(
    Type.Object({
      code: Type.String(),
      message: Type.String(),
    }),
  ),
});
export type ResponseFrame = Static<typeof ResponseFrameSchema>;

/** 服务端推送的事件帧 */
export const EventFrameSchema = Type.Object({
  type: Type.Literal('event'),
  event: NonEmptyString,
  payload: Type.Unknown(),
  seq: Type.Optional(Type.Number()),
  stateVersion: Type.Optional(Type.Record(Type.String(), Type.Number())),
});
export type EventFrame = Static<typeof EventFrameSchema>;

/** 所有帧的联合类型（discriminated on `type`） */
export const GatewayFrameSchema = Type.Union([
  RequestFrameSchema,
  ResponseFrameSchema,
  EventFrameSchema,
]);
export type GatewayFrame = Static<typeof GatewayFrameSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Methods — 请求参数 & 响应 Schema
// ─────────────────────────────────────────────────────────────────────────────

// ── connect ──────────────────────────────────────────────────────────────────

export const ClientInfoSchema = Type.Object({
  id: NonEmptyString,
  displayName: OptionalString,
  version: NonEmptyString,
  platform: NonEmptyString,
  // mode 描述客户端形态；operator/node 是 role，不在此处
  mode: Type.Union([
    Type.Literal('ui'),
    Type.Literal('cli'),
    Type.Literal('node'),
  ]),
  instanceId: OptionalString,
});
export type ClientInfo = Static<typeof ClientInfoSchema>;

export const DeviceIdentitySchema = Type.Object({
  id: NonEmptyString,
  publicKey: NonEmptyString,
  signature: NonEmptyString,
  signedAt: Type.Number(),
  nonce: NonEmptyString,
});
export type DeviceIdentity = Static<typeof DeviceIdentitySchema>;

export const ConnectParamsSchema = Type.Object(
  {
    minProtocol: Type.Number(),
    maxProtocol: Type.Number(),
    client: ClientInfoSchema,
    role: Type.Optional(Type.Union([Type.Literal('operator'), Type.Literal('node')])),
    scopes: Type.Optional(Type.Array(Type.String())),
    /** Node 角色的能力声明（camera, canvas, screen, location, voice 等） */
    caps: Type.Optional(Type.Array(Type.String())),
    /** Node 角色的命令声明 */
    commands: Type.Optional(Type.Array(Type.String())),
    permissions: Type.Optional(Type.Record(Type.String(), Type.Boolean())),
    auth: Type.Optional(Type.Object({ token: NonEmptyString })),
    locale: OptionalString,
    userAgent: OptionalString,
    device: Type.Optional(DeviceIdentitySchema),
  },
  { additionalProperties: false },
);
export type ConnectParams = Static<typeof ConnectParamsSchema>;

export const HelloOkPayloadSchema = Type.Object({
  type: Type.Literal('hello-ok'),
  protocol: Type.Number(),
  server: Type.Object({ version: Type.String(), connId: Type.String() }),
  features: Type.Object({
    methods: Type.Array(Type.String()),
    events: Type.Array(Type.String()),
  }),
  snapshot: Type.Object({
    presence: Type.Array(Type.Unknown()),
    health: Type.Record(Type.String(), Type.Unknown()),
    stateVersion: Type.Record(Type.String(), Type.Number()),
    uptimeMs: Type.Number(),
  }),
  policy: Type.Object({
    maxPayload: Type.Number(),
    maxBufferedBytes: Type.Number(),
    tickIntervalMs: Type.Number(),
  }),
  auth: Type.Optional(
    Type.Object({
      deviceToken: Type.String(),
      role: Type.String(),
      scopes: Type.Array(Type.String()),
    }),
  ),
});
export type HelloOkPayload = Static<typeof HelloOkPayloadSchema>;

// ── health ───────────────────────────────────────────────────────────────────

export const HealthResultSchema = Type.Object({ ok: Type.Boolean() });
export type HealthResult = Static<typeof HealthResultSchema>;

// ── status ───────────────────────────────────────────────────────────────────

export const StatusResultSchema = Type.Object({
  ok: Type.Boolean(),
  version: Type.Optional(Type.String()),
  uptime: Type.Optional(Type.Number()),
  connectedClients: Type.Optional(Type.Number()),
});
export type StatusResult = Static<typeof StatusResultSchema>;

// ── send / poll ──────────────────────────────────────────────────────────────

export const AttachmentSchema = Type.Object({
  type: Type.Optional(Type.String()),
  mimeType: Type.Optional(Type.String()),
  fileName: Type.Optional(Type.String()),
  content: Type.Optional(Type.String()),
  key: Type.Optional(Type.String()),
});
export type Attachment = Static<typeof AttachmentSchema>;

export const SendParamsSchema = Type.Object(
  {
    idempotencyKey: NonEmptyString,
    sessionKey: NonEmptyString,
    message: NonEmptyString,
    deliver: Type.Optional(Type.Boolean()),
    thinking: Type.Optional(Type.Boolean()),
    attachments: Type.Optional(Type.Array(AttachmentSchema)),
    timeoutMs: Type.Optional(Type.Number()),
    channel: Type.Optional(Type.Number()),
  },
  { additionalProperties: false },
);
export type SendParams = Static<typeof SendParamsSchema>;

export const PollParamsSchema = Type.Object(
  {
    idempotencyKey: NonEmptyString,
    sessionKey: NonEmptyString,
    timeoutMs: Type.Optional(Type.Number()),
  },
  { additionalProperties: false },
);
export type PollParams = Static<typeof PollParamsSchema>;

// ── agent / agent.wait ───────────────────────────────────────────────────────

export const AgentParamsSchema = Type.Object(
  {
    idempotencyKey: NonEmptyString,
    sessionKey: NonEmptyString,
    agentId: Type.Optional(Type.String()),
    message: Type.Optional(Type.String()),
    attachments: Type.Optional(Type.Array(AttachmentSchema)),
    label: Type.Optional(Type.String()),
    to: Type.Optional(Type.String()),
    replyTo: Type.Optional(Type.String()),
    replyChannel: Type.Optional(Type.String()),
    accountId: Type.Optional(Type.String()),
    replyAccountId: Type.Optional(Type.String()),
    threadId: Type.Optional(Type.String()),
    groupId: Type.Optional(Type.String()),
    groupChannel: Type.Optional(Type.String()),
    groupspace: Type.Optional(Type.String()),
    lane: Type.Optional(Type.String()),
    spawnedBy: Type.Optional(Type.String()),
    extraSystemPrompt: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);
export type AgentParams = Static<typeof AgentParamsSchema>;

export const AgentWaitParamsSchema = Type.Object(
  {
    idempotencyKey: NonEmptyString,
    sessionKey: NonEmptyString,
    timeoutMs: Type.Optional(Type.Number()),
  },
  { additionalProperties: false },
);
export type AgentWaitParams = Static<typeof AgentWaitParamsSchema>;

// ── chat.send / chat.history / chat.abort / chat.inject ─────────────────────

export const ChatSendParamsSchema = Type.Object(
  {
    idempotencyKey: NonEmptyString,
    sessionKey: NonEmptyString,
    message: NonEmptyString,
    deliver: Type.Optional(Type.Boolean()),
    thinking: Type.Optional(Type.Boolean()),
    attachments: Type.Optional(Type.Array(AttachmentSchema)),
    timeoutMs: Type.Optional(Type.Number()),
    channel: Type.Optional(Type.Number()),
  },
  { additionalProperties: false },
);
export type ChatSendParams = Static<typeof ChatSendParamsSchema>;

export const ChatHistoryParamsSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    limit: Type.Optional(Type.Number()),
  },
  { additionalProperties: false },
);
export type ChatHistoryParams = Static<typeof ChatHistoryParamsSchema>;

export const ChatAbortParamsSchema = Type.Object(
  { sessionKey: NonEmptyString },
  { additionalProperties: false },
);
export type ChatAbortParams = Static<typeof ChatAbortParamsSchema>;

export const ChatInjectParamsSchema = Type.Object(
  {
    idempotencyKey: NonEmptyString,
    sessionKey: NonEmptyString,
    message: NonEmptyString,
    role: Type.Optional(
      Type.Union([Type.Literal('user'), Type.Literal('assistant'), Type.Literal('system')]),
    ),
  },
  { additionalProperties: false },
);
export type ChatInjectParams = Static<typeof ChatInjectParamsSchema>;

// ── sessions.list / sessions.patch / sessions.delete ─────────────────────────

export const SessionsListParamsSchema = Type.Object(
  {
    keys: Type.Optional(Type.Array(Type.String())),
    maxChars: Type.Optional(Type.Number()),
    items: Type.Optional(Type.Number()),
    startDate: Type.Optional(Type.String()),
    endDate: Type.Optional(Type.String()),
    days: Type.Optional(Type.Number()),
  },
  { additionalProperties: false },
);
export type SessionsListParams = Static<typeof SessionsListParamsSchema>;

export const SessionsPatchParamsSchema = Type.Object(
  {
    key: NonEmptyString,
    label: Type.Optional(Type.String()),
    reasoningLevel: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    responseUsage: Type.Optional(
      Type.Union([
        Type.Literal('off'),
        Type.Literal('tokens'),
        Type.Literal('full'),
        Type.Null(),
      ]),
    ),
    tokens: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    elevatedLevel: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  },
  { additionalProperties: false },
);
export type SessionsPatchParams = Static<typeof SessionsPatchParamsSchema>;

export const SessionsDeleteParamsSchema = Type.Object(
  {
    key: NonEmptyString,
    deleteTranscript: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);
export type SessionsDeleteParams = Static<typeof SessionsDeleteParamsSchema>;

// ── node.list / node.invoke / node.pair.* ────────────────────────────────────

export const NodeListParamsSchema = Type.Object(
  {
    limit: Type.Optional(Type.Number()),
    status: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);
export type NodeListParams = Static<typeof NodeListParamsSchema>;

export const NodeInvokeParamsSchema = Type.Object(
  {
    nodeId: NonEmptyString,
    command: NonEmptyString,
    params: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    idempotencyKey: NonEmptyString,
  },
  { additionalProperties: false },
);
export type NodeInvokeParams = Static<typeof NodeInvokeParamsSchema>;

export const NodePairParamsSchema = Type.Object(
  {
    nodeId: NonEmptyString,
    action: Type.Union([Type.Literal('initiate'), Type.Literal('confirm'), Type.Literal('cancel')]),
  },
  { additionalProperties: false },
);
export type NodePairParams = Static<typeof NodePairParamsSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Events — 服务端推送 Schema
// ─────────────────────────────────────────────────────────────────────────────

/** event: tick — 心跳，按 policy.tickIntervalMs 频率推送 */
export const TickEventPayloadSchema = Type.Object({
  ts: Type.Number(),
});
export type TickEventPayload = Static<typeof TickEventPayloadSchema>;

/** event: presence — 在线状态变更 */
export const PresenceEventPayloadSchema = Type.Object({
  clientId: Type.String(),
  status: Type.Union([Type.Literal('online'), Type.Literal('offline')]),
  role: Type.Optional(Type.String()),
  connectedAt: Type.Optional(Type.Number()),
});
export type PresenceEventPayload = Static<typeof PresenceEventPayloadSchema>;

// ── Agent event stream types ──────────────────────────────────────────────────

/** agent event 的 stream 分流字段 */
export type AgentEventStream = 'lifecycle' | 'tool' | 'assistant' | 'error' | (string & {});

// Tool stream — data 因 phase 不同而异

export interface ToolStartData {
  phase: 'start';
  name: string;
  toolCallId: string;
  args: Record<string, unknown>;
}

export interface ToolUpdateData {
  phase: 'update';
  name: string;
  toolCallId: string;
  partialResult?: unknown;
}

export interface ToolResultData {
  phase: 'result';
  name: string;
  toolCallId: string;
  /** 工具元信息，如 exec 会附加 "pty"/"elevated" */
  meta?: string;
  isError: boolean;
  /** 仅 verboseLevel=full 时存在 */
  result?: unknown;
}

export type ToolData = ToolStartData | ToolUpdateData | ToolResultData;

// Assistant stream

export interface AssistantStreamData {
  text: string;
  delta?: unknown;
  mediaUrls?: string[];
}

// Lifecycle stream

export interface LifecycleData {
  phase: 'start' | 'end' | 'error';
  aborted?: boolean;
  stopReason?: string;
  error?: string;
}

// Error stream（seq gap）

export interface SeqGapErrorData {
  reason: 'seq gap';
  expected: number;
  received: number;
}

/** event: agent — 所有 agent 事件（含 tool / lifecycle / assistant / error）共用此结构 */
export const AgentEventPayloadSchema = Type.Object({
  runId: Type.String(),
  seq: Type.Number(),
  stream: Type.String(),
  ts: Type.Number(),
  data: Type.Record(Type.String(), Type.Unknown()),
  sessionKey: Type.Optional(Type.String()),
});
export type AgentEventPayload = Static<typeof AgentEventPayloadSchema>;

/** 单条消息内容 block */
export const ChatContentBlockSchema = Type.Object({
  type: Type.String(),
  text: Type.Optional(Type.String()),
  thinking: Type.Optional(Type.String()),
  thinkingSignature: Type.Optional(Type.String()),
});

/** 服务端推送的消息对象 */
export const ChatMessageObjectSchema = Type.Object({
  role: Type.Union([Type.Literal('user'), Type.Literal('assistant'), Type.Literal('system')]),
  content: Type.Array(ChatContentBlockSchema),
  timestamp: Type.Optional(Type.Number()),
});

/** event: chat — 聊天消息推送，实际结构：state + message 对象 */
export const ChatEventPayloadSchema = Type.Object({
  runId: Type.Optional(Type.String()),
  sessionKey: Type.String(),
  seq: Type.Optional(Type.Number()),
  /** "delta" = 流式分片；"done" = 完整消息 */
  state: Type.Optional(Type.String()),
  message: Type.Optional(ChatMessageObjectSchema),
  idempotencyKey: Type.Optional(Type.String()),
  attachments: Type.Optional(Type.Array(AttachmentSchema)),
});
export type ChatEventPayload = Static<typeof ChatEventPayloadSchema>;

/** event: health — 服务健康状态变更 */
export const HealthEventPayloadSchema = Type.Object({
  ok: Type.Boolean(),
  details: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
});
export type HealthEventPayload = Static<typeof HealthEventPayloadSchema>;

/** event: shutdown — 服务即将关闭 */
export const ShutdownEventPayloadSchema = Type.Object({
  reason: Type.Optional(Type.String()),
  reconnectAfterMs: Type.Optional(Type.Number()),
});
export type ShutdownEventPayload = Static<typeof ShutdownEventPayloadSchema>;

/** event: connect.challenge — 握手挑战（连接后服务端首条消息） */
export const ConnectChallengePayloadSchema = Type.Object({
  nonce: NonEmptyString,
  ts: Type.Number(),
});
export type ConnectChallengePayload = Static<typeof ConnectChallengePayloadSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Method map — 方法名 → 参数类型映射（用于泛型 send 函数）
// ─────────────────────────────────────────────────────────────────────────────

export const GATEWAY_METHODS = [
  // Core
  'connect',
  'health',
  'status',
  // Messaging
  'send',
  'poll',
  'agent',
  'agent.wait',
  // Agents
  'agents.list',
  // Chat
  'chat.send',
  'chat.history',
  'chat.abort',
  'chat.inject',
  // Sessions
  'sessions.list',
  'sessions.patch',
  'sessions.delete',
  // Nodes
  'node.list',
  'node.invoke',
  'node.pair',
  // Debug
  'logs.tail',
] as const;

export type GatewayMethod = (typeof GATEWAY_METHODS)[number];

export const GATEWAY_EVENTS = [
  'connect.challenge',
  'tick',
  'presence',
  'agent',
  'chat',
  'health',
  'shutdown',
] as const;

export type GatewayEvent = (typeof GATEWAY_EVENTS)[number];

/** 方法参数类型映射 */
export interface MethodParams {
  connect: ConnectParams;
  health: Record<string, never>;
  status: Record<string, never>;
  send: SendParams;
  poll: PollParams;
  agent: AgentParams;
  'agent.wait': AgentWaitParams;
  'agents.list': Record<string, never>;
  'chat.send': ChatSendParams;
  'chat.history': ChatHistoryParams;
  'chat.abort': ChatAbortParams;
  'chat.inject': ChatInjectParams;
  'sessions.list': SessionsListParams;
  'sessions.patch': SessionsPatchParams;
  'sessions.delete': SessionsDeleteParams;
  'node.list': NodeListParams;
  'node.invoke': NodeInvokeParams;
  'node.pair': NodePairParams;
  'logs.tail': Record<string, never>;
}

/** 方法结果类型映射 */
export interface MethodResults {
  connect: HelloOkPayload;
  health: HealthResult;
  status: StatusResult;
  send: Record<string, unknown>;
  poll: Record<string, unknown>;
  agent: Record<string, unknown>;
  'agent.wait': Record<string, unknown>;
  'agents.list': Record<string, unknown>;
  'chat.send': Record<string, unknown>;
  'chat.history': Record<string, unknown>;
  'chat.abort': Record<string, unknown>;
  'chat.inject': Record<string, unknown>;
  'sessions.list': Record<string, unknown>;
  'sessions.patch': Record<string, unknown>;
  'sessions.delete': Record<string, unknown>;
  'node.list': Record<string, unknown>;
  'node.invoke': Record<string, unknown>;
  'node.pair': Record<string, unknown>;
  'logs.tail': { lines?: string[] };
}

/** 事件 payload 类型映射 */
export interface EventPayloads {
  'connect.challenge': ConnectChallengePayload;
  tick: TickEventPayload;
  presence: PresenceEventPayload;
  agent: AgentEventPayload;
  chat: ChatEventPayload;
  health: HealthEventPayload;
  shutdown: ShutdownEventPayload;
}
